import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  useTheme,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircleOutline, PlayArrow } from "@mui/icons-material";
import Header from "components/Header";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { getPayouts, updatePayoutStatus } from "service/admin/bookingService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const statusChip = (status) => {
  const map = {
    pending: { label: "Pending", color: "warning" },
    processing: { label: "Processing", color: "info" },
    completed: { label: "Completed", color: "success" },
    failed: { label: "Failed", color: "error" },
  };
  const cfg = map[status] || { label: status || "-", color: "default" };
  return <Chip label={cfg.label} size="small" color={cfg.color} />;
};

const Payouts = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [completeDialog, setCompleteDialog] = useState({
    open: false,
    payoutId: null,
    transactionRef: "",
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getPayouts({ page: page + 1, limit: pageSize });
      const data = response.data.data;
      setRows(Array.isArray(data) ? data : data.data || []);
      if (data.pagination?.total !== undefined) setRowCount(data.pagination.total);
    } catch (error) {
      toast.error("Failed to load payouts.");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleProcess = async (id) => {
    try {
      await updatePayoutStatus(id, "processing", undefined);
      toast.success("Payout marked as processing.");
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: "processing" } : r))
      );
    } catch {
      toast.error("Failed to update payout.");
    }
  };

  const handleComplete = async () => {
    const { payoutId, transactionRef } = completeDialog;
    try {
      await updatePayoutStatus(payoutId, "completed", transactionRef);
      toast.success("Payout marked as completed.");
      setRows((prev) =>
        prev.map((r) =>
          r.id === payoutId
            ? { ...r, status: "completed", transaction_ref: transactionRef }
            : r
        )
      );
      setCompleteDialog({ open: false, payoutId: null, transactionRef: "" });
    } catch {
      toast.error("Failed to complete payout.");
    }
  };

  const columns = [
    {
      field: "facility_name",
      headerName: "FACILITY",
      flex: 1.5,
      renderCell: (params) => params.row.facility?.name || params.value || "-",
    },
    {
      field: "period",
      headerName: "PERIOD",
      flex: 1,
      renderCell: (params) =>
        params.row.period_start && params.row.period_end
          ? `${params.row.period_start} – ${params.row.period_end}`
          : params.value || "-",
    },
    {
      field: "gross_amount",
      headerName: "GROSS",
      flex: 0.8,
      renderCell: (params) => (params.value ? `₹${params.value}` : "-"),
    },
    {
      field: "commission_amount",
      headerName: "COMMISSION",
      flex: 0.9,
      renderCell: (params) => (params.value ? `₹${params.value}` : "-"),
    },
    {
      field: "net_amount",
      headerName: "NET AMOUNT",
      flex: 0.9,
      renderCell: (params) => (params.value ? `₹${params.value}` : "-"),
    },
    {
      field: "bookings_count",
      headerName: "BOOKINGS",
      flex: 0.7,
    },
    {
      field: "status",
      headerName: "STATUS",
      flex: 0.9,
      renderCell: (params) => statusChip(params.value),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 1.2,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          {params.row.status === "pending" && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<PlayArrow />}
              onClick={() => handleProcess(params.row.id)}
            >
              Process
            </Button>
          )}
          {(params.row.status === "pending" || params.row.status === "processing") && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<CheckCircleOutline />}
              onClick={() =>
                setCompleteDialog({
                  open: true,
                  payoutId: params.row.id,
                  transactionRef: "",
                })
              }
            >
              Complete
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <Header title="PAYOUTS" subTitle="Manage facility payouts" />

        <Box
          mt="20px"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            getRowId={(row) => row.id}
            rows={rows}
            columns={columns}
            rowCount={rowCount || rows.length}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode={rowCount > 0 ? "server" : "client"}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(0); }}
            components={{ Toolbar: DataGridCustomToolbar }}
            sx={{
              "& .MuiCircularProgress-circle": { color: theme.palette.secondary[300] },
            }}
          />
        </Box>
      </Box>

      <Dialog
        open={completeDialog.open}
        onClose={() => setCompleteDialog({ open: false, payoutId: null, transactionRef: "" })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mark Payout Complete</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            Enter the transaction reference for this payout.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Transaction Reference"
            color="secondary"
            value={completeDialog.transactionRef}
            onChange={(e) =>
              setCompleteDialog((prev) => ({ ...prev, transactionRef: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setCompleteDialog({ open: false, payoutId: null, transactionRef: "" })
            }
          >
            Cancel
          </Button>
          <Button color="secondary" variant="contained" onClick={handleComplete}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Payouts;
